import * as React from "react";
import { Bell } from "lucide-react";
import { cn } from "../lib/cn";

export interface NotificationBellProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Unread badge count. Hidden when 0/undefined. Capped to "99+" visually. */
  count?: number;
}

/**
 * NotificationBell — chrome-mounted notification trigger with badge.
 *
 * Scaffolding only — wire `onClick` to open the nudges / inbox drawer
 * in feature work (`features/proactive/`). Badge auto-hides when zero.
 */
export const NotificationBell = React.forwardRef<
  HTMLButtonElement,
  NotificationBellProps
>(({ count, className, type, ...rest }, ref) => {
  const showBadge = typeof count === "number" && count > 0;
  const display = showBadge ? (count > 99 ? "99+" : String(count)) : null;
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      aria-label={
        showBadge ? `Notifiche (${display} non lette)` : "Notifiche"
      }
      className={cn(
        "relative inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-2 transition-colors hover:bg-cream-deep hover:text-ink-1",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      )}
      {...rest}
    >
      <Bell className="h-4 w-4" />
      {showBadge ? (
        <span
          className="-right-0.5 -top-0.5 absolute inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent px-1 font-semibold text-[10px] text-accent-foreground leading-none shadow-xs"
          aria-hidden
        >
          {display}
        </span>
      ) : null}
    </button>
  );
});
NotificationBell.displayName = "NotificationBell";
